import React, { useEffect, useState } from 'react';
import { ProgressBar } from '@/components/elements/ProgressBar/ProgressBar';
import { Icon } from '@/components/global/Icons';
import Image from 'next/image';
import extractThumbnailUrlFromVideo from '../../../helpers/extractThumbnailUrlFromVideo';

interface MediaCardProps {
    file: File;
    hasWarning: boolean;
    removeFile: () => void;
    isSubmitting: boolean;
}

const MediaCard = ({ file, hasWarning, removeFile, isSubmitting }: MediaCardProps) => {
    const [thumbnailUrl, setThumbnailUrl] = useState<string>('');
    const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState<boolean>(true);
    const [fileUploadProgressValue, setFileUploadProgressValue] = useState<number>(0);

    useEffect(() => {
        const handleUpdatingFileUploadProgress = () => {
            const reader = new FileReader();

            reader.onprogress = (event) => {
                if (event.lengthComputable) {
                    const loaded = event.loaded;
                    const total = event.total;
                    const percentageLoaded = (loaded / total) * 100;

                    setFileUploadProgressValue(percentageLoaded);
                }
            };

            reader.onerror = () => {
                setFileUploadProgressValue(0);
            };

            reader.readAsDataURL(file);
        };

        const generateThumbnailUrl = async () => {
            let url = '';
            try {
                const blobUrl = URL.createObjectURL(file);
                if (file.type.includes('video')) {
                    url = await extractThumbnailUrlFromVideo(blobUrl);
                } else {
                    url = blobUrl;
                }
            } catch (err) {
                console.error('Failed while generating thumbnail url:', err);
            }
            return url;
        };

        const handleGeneratingThumbnailUrl = async () => {
            setIsGeneratingThumbnail(true);

            try {
                const thumbnail = await generateThumbnailUrl();
                if (!thumbnail) {
                    throw new Error('Failed to generate thumbnail url');
                } else {
                    setThumbnailUrl(thumbnail);
                }
            } catch (err) {
                console.error(err);
            }
            setIsGeneratingThumbnail(false);
        };

        handleUpdatingFileUploadProgress();
        handleGeneratingThumbnailUrl();
    }, [file]);

    return (
        <div className="rounded-2xl bg-shell p-3 relative group mb-4 block min-w-[14rem]">
            <div className="relative flex items-center w-full">
                {isGeneratingThumbnail ? <div className="w-12 h-12 flex items-center justify-center"></div> : <Image src={thumbnailUrl} alt="" className="w-12 h-12" width={1} height={1} />}
                {hasWarning && (
                    <div className="absolute inset-0 flex items-center justify-center bg-opacity-75 w-12 h-12">
                        <Icon id={'alert'} className="w-4 h-4 fill-white" />
                    </div>
                )}
                <div className="max-w-[11rem] sm:max-w-[12rem] ml-2 w-full">
                    <p className="whitespace-nowrap overflow-x-hidden text-sm text-left text-ellipsis font-normal">{file.name}</p>
                    {isGeneratingThumbnail && <ProgressBar value={fileUploadProgressValue} />}
                </div>
                <button disabled={isSubmitting} onClick={removeFile} className="ml-2" type="button">
                    <Icon id="close" className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export { MediaCard };
